import { MfsError, MfsStatusCode } from '@metaos/mfs-telemetry';
import { Change, EditStatus, TreeViewNode } from '@fluid-experimental/tree';
import { MfsBase, MfsItemId } from '..';
import { ElementId, NodeKind } from '../fluid/snapshot';
import { DeltaNodeGroup } from '../fluid/snapshot/deltas';
import { MfsLabelIdInUseError } from '../errors';

export const mfsError = {
  /** Access rule errors */
  conflictingRule: (ruleType: string): Error => new MfsError(`Conflicting ${ruleType} rules`, MfsStatusCode.BadRequest),

  /** Null or whitespace error */
  nullOrWhitespace: (ruleType: string): Error =>
    new MfsError(`${ruleType} must not be null or a whitespace.`, MfsStatusCode.BadRequest),

  /** Query errors */
  emptyMfsQuery: (): Error => new MfsError(`query should specify at least one option`, MfsStatusCode.NotAllowed),

  /** Empty select */
  emptySelectQuery: (): Error =>
    new MfsError(`select should specify at least one app property set`, MfsStatusCode.NotAllowed),

  /** Positive integer required. */
  mustBePositiveInteger: (name: string): Error =>
    new MfsError(`Name must be an integer value greater than 0`, MfsStatusCode.BadRequest),

  /** Invalid stable ID */
  invalidStableNodeId: (invalidStableNodeId: string, compressedNodeId?: string): Error =>
    new MfsError(
      compressedNodeId
        ? `Node ID ${compressedNodeId} produced an invalid Stable Node ID ${invalidStableNodeId}`
        : `Invalid Stable Node ID ${invalidStableNodeId}`,
      MfsStatusCode.NotAllowed
    ),

  /** Invalid collection element-position. */
  invalidPosition: (): Error => new MfsError(`Invalid position provided.`, MfsStatusCode.BadRequest),

  /** Invalid collection kind. */
  invalidCollectionKind: (): Error =>
    new MfsError(`Collection kind value is not recognized.`, MfsStatusCode.BadRequest),

  /** Invalid collection element values. */
  invalidCollectionValues: (): Error =>
    new MfsError(`Collection values should be an array of Mfs Item Ids.`, MfsStatusCode.BadRequest),

  /** Cycles are not allowed. */
  circularReference: (): Error => new MfsError(`Circular references are not allowed.`, MfsStatusCode.BadRequest),

  /** Invalid move. */
  invalidMove: (): Error =>
    new MfsError(
      'Invalid move attempted. Make sure the elementIds in `from` and `to` are different and `from` is not an ancestor of `to`.',
      MfsStatusCode.BadRequest
    ),

  /** Invalid query depth value. */
  invalidQueryDepth: (depth: number): Error =>
    new MfsError(`Invalid depth: ${depth}. Depth should be a positive integer >= 1`, MfsStatusCode.BadRequest),

  /** Creation */
  systemPropertiesProvided: (): Error =>
    new MfsError(`Providing system properties in item is not allowed`, MfsStatusCode.NotAllowed),

  /** Empty values are not allowed in argument. */
  emptyValues: (argumentName: string): Error => new MfsError(`Empty values are not allowed.`, MfsStatusCode.BadRequest),

  /** Max input length error. */
  maxInputLengthExceeded: (argumentName: string): Error =>
    new MfsError(`Max input length exceeded`, MfsStatusCode.BadRequest),

  /** Runtime assertion failed. */
  assertionFailed: (): Error => new MfsError(`Assertion failed`, MfsStatusCode.BadRequest),

  /** Invalid item id. */
  invalidItemId: (): Error => new MfsError(`Invalid item id provided`, MfsStatusCode.BadRequest),

  /** Attempted to read an item which was missing some required properties. */
  invalidItem: (): Error =>
    new MfsError(`Attempted to read an item which was missing required properties.`, MfsStatusCode.BadRequest),

  /** Invalid secondary label . */
  invalidSecLabel: (): Error =>
    new MfsError(`Invalid label value provided in secondary labels.`, MfsStatusCode.BadRequest),

  /** Invalid label. */
  invalidLabel: (): Error => new MfsError(`Invalid label provided.`, MfsStatusCode.BadRequest),

  /** Invalid label id. */
  invalidLabelId: (): Error => new MfsError(`Invalid label id provided.`, MfsStatusCode.BadRequest),

  /** Invalid displayText. */
  invalidDisplayText: (): Error => new MfsError(`Invalid display text value provided.`, MfsStatusCode.BadRequest),

  /** Invalid delta for secondary label. */
  invalidSecLabelsDelta: (): Error =>
    new MfsError(
      `Invalid label value provided in the add/remove values for secondary labels.`,
      MfsStatusCode.BadRequest
    ),

  /** Invalid (empty) delta value. */
  invalidMfsDeltaValue: (): Error =>
    new MfsError(
      `Invalid delta value provided. At least one of values to add or remove must be defined.`,
      MfsStatusCode.BadRequest
    ),

  /** Item id does not exist. */
  itemNotFound: (id: string): Error => new MfsError(`item id: ${id} does not exist`, MfsStatusCode.NotFound),

  /** Item with id is not a collection item. */
  notCollectionItem: (id: string): Error =>
    new MfsError(`Item with id:${id} is not a collection item.`, MfsStatusCode.BadRequest),

  /** Label + Label Id pair are already in use by another item. */
  labelIdInUse: (itemId: string, label: string, labelId: string): Error => new MfsLabelIdInUseError(itemId),

  /** Invalid edge label. */
  invalidEdgeLabel: (): Error => new MfsError(`Two way edge label expected`, MfsStatusCode.UnhandledException),

  /** Cannot set/delete property. */
  accessDenied: (accessType: 'set' | 'delete', name: string): Error =>
    new MfsError(`Cannot ${accessType} for property`, MfsStatusCode.Forbidden),

  /** Delta not allowed for single value property. */
  deltaNotAllowed: (propertyName: keyof MfsBase): Error =>
    new MfsError(`Delta not allowed for single-value property`, MfsStatusCode.BadRequest),

  /** Invalid app prefix in host config. */
  invalidAppPrefix: (): Error =>
    new MfsError(
      'Invalid app prefix value provided. Make sure you provide a non-empty string consisting of only letters.',
      MfsStatusCode.BadRequest
    ),

  /** Invalid app id in host config. */
  invalidAppId: (): Error =>
    new MfsError(
      'Invalid app id value provided. Make sure you provide a globally unique app identifier value.',
      MfsStatusCode.BadRequest
    ),

  /** Cannot create a collection using this method. */
  invalidCreationMethod: (): Error =>
    new MfsError('Cannot create a collection using this method.', MfsStatusCode.BadRequest),

  /** Unrecognized change sequence received. */
  unrecognizedChangeSequence: (message: string): Error =>
    new MfsError(`Unrecognized change sequence received ${message}`, MfsStatusCode.UnhandledException),

  /** Edit was not applied because it was invalid or malformed */
  droppedEdit: (result: EditStatus, changes: readonly Change[]): Error =>
    result === EditStatus.Invalid
      ? new MfsError(
          `Concurrent changes caused one of more merge conflicts and the edit was not applied to the current view. Please retry the operation.`,
          MfsStatusCode.Conflict
        )
      : new MfsError(`Malformed edit detected`, MfsStatusCode.UnhandledException),

  /** Expected single-valued property and found multi-valued or vice-versa. */
  invalidPropertyKind: (expected: NodeKind): Error =>
    new MfsError(`the property was not of expected kind: ${expected}`, MfsStatusCode.BadRequest),

  /** Found a property with mixed values (single-values & multi-values). */
  malformedProperty: (): Error =>
    new MfsError(
      `Property was added both as a single-value & multi-valued property. Delete & recreate the affected property. }`,
      MfsStatusCode.DataCorruption
    ),

  /** Parent collection not found. */
  parentCollectionNotFound: (elementId: ElementId, collection: 'Previous' | 'Current'): Error =>
    new MfsError(`${collection} parent collection not found for elementId: ${elementId}`, MfsStatusCode.DataCorruption),

  /** Parent node not found in snapshot. */
  orphanedNode: (nodeId: string): Error =>
    new MfsError(`The parent of nodeId:${nodeId} was not found in snapshot`, MfsStatusCode.DataCorruption),

  /** Items index containing non-existing item node entries. */
  indexCorruption: (itemId: MfsItemId, itemNodeIdValue: string): Error =>
    new MfsError(
      `Cannot read the item node indexed by (itemId: ${itemId} -> itemNodeId: ${itemNodeIdValue})`,
      MfsStatusCode.DataCorruption
    ),

  /** Unrecognized delta. */
  unrecognizedDelta: (delta: DeltaNodeGroup): Error =>
    new MfsError(`Unrecognized delta:`, MfsStatusCode.UnhandledException),

  /** Node not found. */
  nodeNotFound: (expected: NodeKind, found?: TreeViewNode): Error =>
    new MfsError(`Expected node not found`, MfsStatusCode.DataCorruption),

  /** You can only save up to max items in the boot index. */
  maxBootKeys: (max: number, keysInUse: string[]): Error =>
    new MfsError(
      `You can only save up to ${max} keys in the boot index. Currently in use: ${keysInUse.length}.`,
      MfsStatusCode.NotAllowed
    ),

  /** Invalid boot index value. */
  invalidBootValue: (): Error =>
    new MfsError('Invalid boot index value. Only item id values are allowed.', MfsStatusCode.BadRequest),

  /** Method not implemented */
  notImplemented: (): Error => new MfsError('Method not implemented', MfsStatusCode.NotImplemented),

  /** Failure to uniquely claim/release a label & label id for a given item id. */
  consensusError: (
    mode: 'claim' | 'release',
    label: string,
    labelId: string,
    itemId: MfsItemId,
    keys: string[]
  ): Error => new MfsError(`Failed to ${mode} item id: '${itemId}' due to consensus error.`, MfsStatusCode.Consensus),
};
